import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  Home, 
  Layers, 
  Droplets, 
  Zap, 
  Paintbrush, 
  Square, 
  Wind, 
  Building,
  Hammer,
  ArrowRight 
} from 'lucide-react';

export const ServicesPreview = () => {
  const [activeService, setActiveService] = useState('hvac');

  const services = [
    {
      id: 'hvac',
      icon: Wind,
      title: 'AC Install & Repair',
      description: 'Install, replace, service HVAC systems (all major brands), ductwork repair/replacement, mini-split systems, thermostats, post-renovation integration.',
      image: 'https://images.unsplash.com/photo-1757219525975-03b5984bc6e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxIVkFDJTIwZHVjdHdvcmslMjBhaXIlMjBjb25kaXRpb25pbmclMjBpbnN0YWxsYXRpb258ZW58MHx8fHwxNzc0NDUxMzE4fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      id: 'carpentry',
      icon: Hammer,
      title: 'Carpentry',
      description: 'Custom trim, built-ins, finish carpentry, new decks, cabinet installation, crown molding, doors, windows.',
      image: 'https://customer-assets.emergentagent.com/job_construct-hub-276/artifacts/5b66qqme_1000184564.jpg',
    },
    {
      id: 'electrical',
      icon: Zap,
      title: 'Electrical',
      description: 'Wiring up to code, new panels, circuits, lighting/fixture work, permit-pulled, inspections, EV charger installation.',
      image: 'https://customer-assets.emergentagent.com/job_construct-hub-276/artifacts/cwv69y4r_1000184595.jpg',
    },
    {
      id: 'flooring',
      icon: Layers,
      title: 'Flooring',
      description: 'Hardwood, engineered wood, tile, luxury vinyl plank (LVP), laminate, carpet, subfloor prep, transitions, and custom patterns.',
      image: 'https://images.pexels.com/photos/1388944/floor-flooring-hand-man-1388944.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      id: 'renovation',
      icon: Building,
      title: 'Home Renovation',
      description: 'Kitchen remodels, bathroom renovations, full additions, basement/garage conversions, commercial tenant improvements.',
      image: 'https://customer-assets.emergentagent.com/job_construct-hub-276/artifacts/mx4uyftl_1000184558.jpg',
    },
    {
      id: 'painting',
      icon: Paintbrush,
      title: 'Painting',
      description: 'Interior/exterior painting, proper prep, primer, sanding, trim work, cabinet painting/refinishing, deck/fence staining, texture matching.',
      image: 'https://customer-assets.emergentagent.com/job_construct-hub-276/artifacts/g404ppk0_1000184592.jpg',
    },
    {
      id: 'plumbing',
      icon: Droplets,
      title: 'Plumbing',
      description: 'Dripping faucet to full-home re-pipes, water heater install/replacement, fixture/faucet installation, kitchen/bath integration, drain cleaning.',
      image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?q=80&w=800',
    },
    {
      id: 'roofing',
      icon: Home,
      title: 'Roofing',
      description: 'Hail damage assessment, full replacement, new installs, asphalt shingles, metal roofing, flat/TPO systems, written warranty, post-install inspection.',
      image: 'https://images.unsplash.com/photo-1726589004565-bedfba94d3a2?q=80&w=800',
    },
    {
      id: 'sheetrock',
      icon: Square,
      title: 'Sheetrock / Drywall',
      description: 'Patching holes, hanging/finishing rooms, texture matching (orange peel, knockdown, smooth), water damage replacement, soundproofing.',
      image: 'https://images.pexels.com/photos/4981812/pexels-photo-4981812.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
  ];

  const activeServiceData = services.find(s => s.id === activeService);

  return (
    <section data-testid="services-preview" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-copper text-xs font-inter uppercase tracking-widest">
            Services
          </span>
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-navy mt-4">
            What we do
          </h2>
          <p className="text-navy/60 font-inter mt-4">
            Every trade. One standard. No excuses.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Image */}
          <div className="relative overflow-hidden bg-navy/5 aspect-square lg:aspect-[4/5]">
            <img
              src={activeServiceData?.image}
              alt={activeServiceData?.title}
              className="w-full h-full object-cover transition-all duration-500"
              data-testid="service-image"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-navy/80 to-transparent p-8">
              <h3 className="text-2xl font-playfair font-bold text-white">
                {activeServiceData?.title}
              </h3>
            </div>
          </div>

          {/* Accordion */}
          <div>
            <Accordion 
              type="single" 
              collapsible 
              value={activeService}
              onValueChange={(value) => value && setActiveService(value)}
              className="space-y-2"
              data-testid="services-accordion"
            >
              {services.map((service) => (
                <AccordionItem 
                  key={service.id} 
                  value={service.id}
                  className="border border-tan/20 data-[state=open]:border-copper"
                >
                  <AccordionTrigger 
                    className="px-6 py-4 hover:no-underline group"
                    data-testid={`service-trigger-${service.id}`}
                  >
                    <div className="flex items-center space-x-4">
                      <service.icon className="w-5 h-5 text-copper" />
                      <span className="font-playfair font-semibold text-navy group-hover:text-copper transition-colors">
                        {service.title}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <p className="text-navy/70 font-inter text-sm leading-relaxed pl-9">
                      {service.description}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <Link to="/services" className="block mt-8">
              <Button 
                data-testid="services-cta"
                className="w-full bg-navy hover:bg-navy/90 text-white px-8 py-6 text-xs font-bold uppercase tracking-widest group"
              >
                View All Services
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;
